import * as UpChunk from '@mux/upchunk';
import { AssignedVideo, GroupVideo, Video } from 'src/models/video/_types';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';

import { Group } from '../group/_types';
import { normalize } from '../shared/normalizers';

import {
  denormalizeGroupVideo,
  denormalizeVideo,
  normalizeAssignedVideo,
  normalizeVideo,
} from './normalizers';

export const getVideoUploadUrl = async (videoTypeParams?: {
  videoType?: string;
  videoTypeId?: string | number;
  videoId?: string;
}) => {
  const params = {
    download: true,
    ...(videoTypeParams?.videoType && {
      video_type: videoTypeParams.videoType,
    }),
    ...(videoTypeParams?.videoTypeId && {
      video_type_id: videoTypeParams.videoTypeId.toString(),
    }),
    ...(videoTypeParams?.videoId && {
      video_id: videoTypeParams.videoId,
    }),
  };

  const response = await api.post('/api/v4/videos/upload_url', null, {
    params,
  });
  return response.data;
};

let currentUpload: UpChunk.UpChunk | null = null;
export const uploadVideo = async ({
  url,
  file,
  progressCallback,
  errorCallback,
}): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (currentUpload) {
      currentUpload.abort();
    }
    if (!file) return;
    const upload = UpChunk.createUpload({
      endpoint: url, // Authenticated url
      file: file, // File object with your video file's properties
      chunkSize: 10240, // Uploads the file in ~30 MB chunks
    });

    currentUpload = upload;

    upload.on('progress', (progress) => {
      progressCallback && progressCallback(progress.detail);
    });

    upload.on('error', (error) => {
      errorCallback && errorCallback(error.detail);
      reject(error.detail);
    });
    upload.on('success', () => {
      resolve();
    });

    return upload;
  });
};

interface FetchVideoOptions {
  id: Video['id'];
}

export const fetchVideo = async ({ id }: FetchVideoOptions): Promise<Video> => {
  const response = await api.get(`/api/v4/videos/${id}`);
  const video = normalizeVideo(response.data);
  return video;
};

interface FetchAssignedVideoOptions {
  userId?: Identifier | undefined;
  fetchAll?: boolean;
  page?: number;
  perPage?: number;
  search?: string;
  tags?: string[];
}

export const fetchAssignedVideos = async ({
  fetchAll,
  userId,
  page,
  perPage,
  search,
  tags,
}: FetchAssignedVideoOptions): Promise<
  AssignedVideo[] | { data: AssignedVideo[]; total: number }
> => {
  const response = await api.get(`/api/v4/assigned_videos/`, {
    params: {
      fetch_all: fetchAll,
      user_id: userId,
      page,
      per: perPage,
      'q[video_tags_name_eq_any]': tags,
      'q[video_name_cont]': search,
      ...(search && { 'q[s]': 'created_at desc' }),
    },
  });
  if (Array.isArray(response.data)) {
    return response.data.map(normalizeAssignedVideo);
  }
  return {
    data: response.data.assigned_video.map(normalizeAssignedVideo),
    total: response.data.meta.total,
  };
};

export const fetchGroupVideos = async ({
  fetchAll,
  search,
  tags,
}: {
  fetchAll?: boolean;
  search?: string;
  tags?: string[];
}): Promise<Video[]> => {
  const response = await api.get(`/api/v3/group_videos`, {
    params: {
      fetch_all: fetchAll,
      for_user: true,
      'q[tags_name_eq_any]': tags,
      'q[name_cont]': search,
      ...(search && { 'q[s]': 'created_at desc' }),
    },
  });
  return response.data.map(normalize);
};

export const fetchGroupVideosByGroupId = async ({
  groupId,
  search,
  tags,
  sortBy,
  sortDir,
  page,
  perPage,
  matchAllTags,
}: {
  groupId: Group['id'];
  search?: string;
  tags?: string[];
  sortBy?: string;
  sortDir?: string;
  page?: number;
  perPage?: number;
  matchAllTags?: boolean;
}): Promise<{ groupVideos: Video[]; total: number }> => {
  const tagParams = matchAllTags
    ? { 'q[tags_name_eq_all]': tags }
    : { 'q[tags_name_eq_any]': tags };

  const response = await api.get(`/api/v3/group_videos`, {
    params: {
      group_id: groupId,
      ...tagParams,
      'q[name_cont]': search,
      'q[s]': sortBy && sortDir && `${sortBy} ${sortDir}`,
      page,
      per: perPage,
    },
  });

  const normalizedVideos = response.data?.groupvideo?.map((groupvideo) =>
    normalize(groupvideo)
  );

  return {
    groupVideos: normalizedVideos,
    total: response.data?.meta?.total,
  };
};

export const fetchGroupVideo = async ({
  groupVideoId,
}: {
  groupVideoId: GroupVideo['id'];
}): Promise<GroupVideo> => {
  const response = await api.get(`/api/v3/group_videos/${groupVideoId}`);
  return normalize(response.data);
};

export const handleGroupVideoFileUpload = async (
  file: File
): Promise<{ id: string; url: string }> => {
  const formData = new FormData();
  formData.append('asset_image[image]', file);
  const response = await api.post(
    '/api/v2/assets/image/group-video',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

export const newGroupVideo = async ({
  data,
}: {
  data: Partial<GroupVideo>;
}) => {
  const payload = denormalizeGroupVideo(data);

  const response = await api.post(`/api/v3/group_videos`, {
    group_video: {
      ...payload,
      tag_list: data.tags?.join(','),
    },
  });
  return normalize(response.data);
};

export const updateGroupVideo = async ({
  groupVideoId,
  data,
}: {
  groupVideoId: GroupVideo['id'];
  data: Partial<GroupVideo>;
}) => {
  const payload = denormalizeGroupVideo(data);

  const response = await api.put(`/api/v3/group_videos/${groupVideoId}`, {
    group_video: {
      ...payload,
      tag_list: data.tags?.join(','),
    },
  });
  return normalize(response.data);
};

export const deleteGroupVideo = async ({
  groupVideoId,
}: {
  groupVideoId: Video['id'];
}) => {
  await api.delete(`/api/v3/group_videos/${groupVideoId}`);
};

interface DeleteAssignedVideoOptions {
  assignedVideoId: Identifier | undefined;
}

export const deleteAssignedVideo = async ({
  assignedVideoId,
}: DeleteAssignedVideoOptions): Promise<AssignedVideo> => {
  const response = await api.delete(
    `/api/v4/assigned_videos/${assignedVideoId}`
  );
  return normalizeAssignedVideo(response.data);
};

interface FetchVideosOptions {
  direct_upload: boolean;
  fetch_all: boolean;
  pluck_columns?: string;
  sort?: string;
}

export const fetchVideos = async ({
  direct_upload,
  fetch_all,
  pluck_columns,
  sort,
}: FetchVideosOptions): Promise<Video[]> => {
  const response = await api.get('/api/v3/videos', {
    params: {
      fetch_all,
      direct_upload,
      ...(pluck_columns && { pluck_columns }),
      ...(sort && { q: { s: sort } }),
    },
  });

  return response.data.map(normalizeVideo);
};

interface FetchVideosByParamsOptions {
  directUpload: boolean;
  page?: number;
  perPage?: number;
  tags?: string[];
  sortBy?: string;
  sortDir?: string;
  search?: string;
}
export const fetchVideosByParams = async ({
  directUpload,
  page,
  perPage,
  tags,
  sortBy,
  sortDir,
  search,
}: FetchVideosByParamsOptions): Promise<{ total: number; videos: Video[] }> => {
  const response = await api.get('/api/v3/videos', {
    params: {
      direct_upload: directUpload,
      page,
      per: perPage,
      'q[tags_name_eq_any]': tags,
      'q[s]': sortBy && sortDir && `${sortBy} ${sortDir}`,
      'q[name_cont]': search,
    },
  });

  return {
    total: response.data.meta.total,
    videos: response.data.video.map(normalizeVideo),
  };
};

interface CreateAssignedVideoOptions {
  videoId: Identifier | undefined;
  userId: Identifier | undefined;
  message?: string;
  notify: boolean;
}

export const createAssignedVideo = async ({
  videoId,
  userId,
  message,
  notify,
}: CreateAssignedVideoOptions): Promise<AssignedVideo> => {
  const response = await api.post('/api/v4/assigned_videos', {
    assigned_video: {
      video_id: videoId,
      user_id: userId,
      message,
      notify,
    },
  });
  return normalizeAssignedVideo(response.data);
};

interface UpdateVideoOptions {
  id: Identifier | undefined;
  name?: string;
  params?: Partial<Video>;
}

export const updateVideo = async ({
  id,
  name,
  params,
}: UpdateVideoOptions): Promise<Video> => {
  const response = await api.put(`/api/v3/videos/${id}`, {
    video: { name, ...params },
  });
  return normalizeVideo(response.data);
};

interface BulkUpdateVideosOptions {
  videoIds: Identifier[];
  groupIds: Identifier[];
  tags: string[];
  delayInterval: string;
  expiresAt: string;
}
export const bulkUpdateVideos = async ({
  videoIds,
  groupIds,
  tags,
  delayInterval,
  expiresAt,
}: BulkUpdateVideosOptions): Promise<{ success: string }> => {
  const response = await api.get(`/api/v3/videos/bulk_update`, {
    params: {
      video_ids: videoIds,
      group_ids: groupIds,
      tags,
      delay_interval: delayInterval,
      expires_at: expiresAt,
    },
  });
  return normalize(response.data);
};

interface CreateVideoOptions {
  name?: string;
  url?: string;
  thumbnailUrl?: string;
  data?: Partial<Video>;
}

export const createVideo = async ({
  name,
  url,
  thumbnailUrl,
  data,
}: CreateVideoOptions): Promise<Video> => {
  const { tags, ...rest } = data || {};
  const response = await api.post('/api/v3/videos', {
    video: denormalizeVideo({
      name,
      url,
      thumbnail_url: thumbnailUrl,
      tag_list: tags?.join(','),
      ...rest,
    }),
  });

  return normalizeVideo(response.data);
};

export const deleteVideo = async (id: Video['id']): Promise<Video> => {
  const response = await api.delete(`/api/v3/videos/${id}`);
  return normalizeVideo(response.data);
};
